import { useCallback } from 'react';
import { computeMultiCampaignFullTidyState } from './multiCampaignTidyState';
import { flushTidyPositionChanges } from './flushTidySaves';
import { scheduleSingleCampaignFullTidyPersist } from './scheduleSingleCampaignFullTidyPersist';
import { runPartialTidyCompactionAndSave } from './partialTidyPersist';
import {
  markCampaignTidyManaged,
  clearCampaignTidyManaged,
} from '../flowchartLayout/tidyManagedCampaigns';

/**
 * Tidy / auto-layout handlers with persistence (multi-campaign, full, partial).
 */
export function usePositionPersistenceAutoLayout({
  tasks,
  autoPositionsRef,
  onPositionChange,
  onPositionChangeBatch,
  interaction,
  triggerLayout,
  triggerLayoutInstant,
  interactionAutoLayout,
  isMultiCampaignMode,
  nodeDimensions,
  onAnimationStart,
  onAnimationEnd,
  elkPositionsOkRef,
  fullTidyPersistGenerationRef,
  campaignId = null,
}) {
  const handleAutoLayout = useCallback(
    (overrideSelectedNodes = null, options = {}) => {
      if (!interaction || !interactionAutoLayout) return;

      // Only a caller-supplied id set is chain-scoped. Canvas selection never
      // changes tidy scope (select + tidy was a broken user path).
      const selectedNodes = overrideSelectedNodes instanceof Set
        ? overrideSelectedNodes
        : new Set();
      const hasPartialSelection =
        selectedNodes.size > 0 && selectedNodes.size < tasks.length;

      if (isMultiCampaignMode && !hasPartialSelection) {
        const { allPositionChanges, displayPositions } = computeMultiCampaignFullTidyState(
          tasks,
          nodeDimensions,
        );

        if (interaction.setCustomPositions) {
          interaction.setCustomPositions(displayPositions);
        }
        triggerLayout?.();

        setTimeout(() => {
          flushTidyPositionChanges(
            allPositionChanges,
            { skipRecordAction: options.skipRecordAction },
            onPositionChangeBatch,
            onPositionChange,
          );
        }, 50);
        return;
      }

      if (hasPartialSelection) {
        interactionAutoLayout(overrideSelectedNodes);
      }

      if (!hasPartialSelection) {
        // Full Tidy: the board now follows ELK — future dimension-driven layout shifts
        // may silently re-align it (see useSnapSavedToElkOnSettle).
        markCampaignTidyManaged(campaignId);
        // Drop stale drag/runtime overrides so ELK + edge pathD use one coordinate system.
        // Otherwise custom wins in useNodePositions and edges can jump when persist overwrites.
        if (interaction.setCustomPositions) {
          interaction.setCustomPositions({});
        }
        triggerLayout?.();
        const tidyGeneration = ++fullTidyPersistGenerationRef.current;
        const tidyStartedAt = Date.now();
        scheduleSingleCampaignFullTidyPersist({
          tidyGeneration,
          fullTidyPersistGenerationRef,
          tidyStartedAt,
          isMultiCampaignMode,
          elkPositionsOkRef,
          autoPositionsRef,
          interaction,
          tasks,
          onPositionChangeBatch,
          onPositionChange,
          options,
          skipSave: false,
        });
      } else {
        // Partial tidy arranges only a selection — the board no longer matches full ELK.
        clearCampaignTidyManaged(campaignId);
        runPartialTidyCompactionAndSave({
          interaction,
          tasks,
          nodeDimensions,
          onPositionChangeBatch,
          onPositionChange,
          onAnimationStart,
          onAnimationEnd,
          skipSave: false,
          options,
          animateTidy: true,
        });
      }
    },
    [
      interactionAutoLayout,
      triggerLayout,
      interaction,
      tasks,
      onPositionChange,
      onPositionChangeBatch,
      isMultiCampaignMode,
      nodeDimensions,
      onAnimationStart,
      onAnimationEnd,
      elkPositionsOkRef,
      fullTidyPersistGenerationRef,
      autoPositionsRef,
      campaignId,
    ],
  );

  const handleAutoLayoutInstant = useCallback(
    (options = {}) => {
      if (!interaction || !interactionAutoLayout) return;

      const { skipSave = false } = options;
      const hasPartialSelection = false;

      if (isMultiCampaignMode && !hasPartialSelection) {
        const { allPositionChanges, displayPositions } = computeMultiCampaignFullTidyState(
          tasks,
          nodeDimensions,
        );

        if (interaction.setCustomPositions) {
          interaction.setCustomPositions(displayPositions);
        }
        triggerLayoutInstant?.();

        if (!skipSave) {
          setTimeout(() => {
            flushTidyPositionChanges(
              allPositionChanges,
              { skipRecordAction: options.skipRecordAction },
              onPositionChangeBatch,
              onPositionChange,
            );
          }, 50);
        }
        return;
      }

      if (!hasPartialSelection) {
        // Same as animated full Tidy: drop drag overrides so ELK owns placement.
        // Do not run interactionAutoLayout — after paste that would tidy only the
        // current selection and write customPositions that beat ELK.
        if (interaction.setCustomPositions) {
          interaction.setCustomPositions({});
        }
        if (!skipSave) markCampaignTidyManaged(campaignId);
        triggerLayoutInstant?.();
        const tidyGeneration = ++fullTidyPersistGenerationRef.current;
        const tidyStartedAt = Date.now();
        scheduleSingleCampaignFullTidyPersist({
          tidyGeneration,
          fullTidyPersistGenerationRef,
          tidyStartedAt,
          isMultiCampaignMode,
          elkPositionsOkRef,
          autoPositionsRef,
          interaction,
          tasks,
          onPositionChangeBatch,
          onPositionChange,
          options,
          skipSave,
        });
      } else {
        if (!skipSave) clearCampaignTidyManaged(campaignId);
        runPartialTidyCompactionAndSave({
          interaction,
          tasks,
          nodeDimensions,
          onPositionChangeBatch,
          onPositionChange,
          onAnimationStart: null,
          onAnimationEnd: null,
          skipSave,
          options: {},
          animateTidy: false,
        });
      }
    },
    [
      interactionAutoLayout,
      triggerLayoutInstant,
      interaction,
      tasks,
      onPositionChange,
      onPositionChangeBatch,
      isMultiCampaignMode,
      nodeDimensions,
      elkPositionsOkRef,
      fullTidyPersistGenerationRef,
      autoPositionsRef,
      campaignId,
    ],
  );

  return { handleAutoLayout, handleAutoLayoutInstant };
}
